import type { DiscoverySuggestionsResponseType } from "@sellar-io/api-v2/types";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useDebounce } from "use-debounce";
import { useFirebaseUser } from "~/hooks/useFirebaseUser.shared";
import { useSellarCurrentBusiness } from "~/hooks/useSellarCurrentBusiness.shared";
import {
  apiV2AssertResponse,
  apiV2Request,
  getDefaultInfiniteQueryOptionsV2,
} from "../apiV2Request";
import { ServerStateKeys } from "../ServerStateKeys";

export const useMarketSearchSuggestions = ({
  search,
  postcode,
  enabled,
  scope = "market",
}: {
  search: string;
  postcode?: string;
  enabled: boolean;
  scope?: "market" | "suppliers";
}) => {
  const { user, loading: userLoading } = useFirebaseUser();
  const { business, loading: businessLoading } = useSellarCurrentBusiness();
  const [debouncedSearch] = useDebounce(search.trim(), 200);
  const searchSettled = search.trim() === debouncedSearch;
  const validSearchLength =
    debouncedSearch.length === 0 ||
    ((debouncedSearch.length >= 2 || scope === "suppliers") &&
      debouncedSearch.length <= 100);
  const contextLoaded = !userLoading && !businessLoading;
  const isSupplier = Boolean(business?.Supplier?.id);
  const canFetchSuggestions =
    enabled &&
    contextLoaded &&
    searchSettled &&
    validSearchLength &&
    !isSupplier;
  const query = useInfiniteQuery({
    ...getDefaultInfiniteQueryOptionsV2<DiscoverySuggestionsResponseType>({
      context: { scope: "nonSupplier", user, business },
      options: {
        limit: undefined,
        enabled: canFetchSuggestions,
        retry: false,
        staleTime: debouncedSearch ? 0 : 30_000,
      },
    }),
    initialPageParam: 0,
    getNextPageParam: (
      lastPage: DiscoverySuggestionsResponseType,
      pages: DiscoverySuggestionsResponseType[],
    ) =>
      lastPage.hasMoreSuppliers
        ? pages.reduce((count, page) => count + page.suppliers.length, 0)
        : undefined,
    queryKey: [
      ServerStateKeys.DiscoverySuggestions,
      user?.uid,
      user?.isAdmin,
      business?.id,
      business?.BusinessUser?.role,
      business?.Retailer?.id,
      business?.Supplier?.id,
      postcode,
      scope,
      debouncedSearch,
    ],
    queryFn: ({ signal, pageParam }) =>
      apiV2Request({
        user,
        business,
        signal,
        method: "get",
        path: "discovery/suggestions",
        params: {
          search: debouncedSearch,
          retailerPostcode: postcode,
          scope,
          offset: scope === "suppliers" ? pageParam : 0,
        },
      }).then(
        (response) =>
          apiV2AssertResponse<DiscoverySuggestionsResponseType>(response).data,
      ),
  });
  return {
    data:
      canFetchSuggestions && query.data
        ? {
            ...query.data.pages[0],
            suppliers: query.data.pages.flatMap((page) => page.suppliers),
            products: query.data.pages.flatMap((page) => page.products),
          }
        : undefined,
    hasMore: canFetchSuggestions && query.hasNextPage,
    loadMore: () => query.fetchNextPage(),
    isLoadingMore: query.isFetchingNextPage,
    isLoading:
      enabled &&
      (search.trim().length >= 2 || scope === "suppliers") &&
      (!canFetchSuggestions || (query.isFetching && !query.isFetchingNextPage)),
    isError: canFetchSuggestions && query.isError,
  };
};
